import sidebarConfig, {
  routeOnlyPermissionItems,
} from 'src/layouts/dashboard/SidebarConfig';
import { PATH_DASHBOARD } from 'src/routes/paths';

/**
 * Flatten sidebar nav items (including children) into { path, hidden } entries.
 * Reuses the same permission rules as the dashboard menu — do not duplicate IDs here.
 * Also merges route-only rules (e.g. Settings) that are gated but not in the nav.
 */
export function getSidebarPermissionItems(config = sidebarConfig) {
  const items = [];

  config.forEach((group) => {
    (group.items || []).forEach((item) => {
      if (item?.path) {
        items.push({ path: item.path, hidden: item.hidden });
      }
      (item.children || []).forEach((child) => {
        if (child?.path) {
          items.push({ path: child.path, hidden: child.hidden });
        }
      });
    });
  });

  routeOnlyPermissionItems.forEach((item) => {
    if (item?.path) {
      items.push({ path: item.path, hidden: item.hidden });
    }
  });

  return items;
}

const normalizePath = (pathname) => {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

/** Shared post-login landing page — open to every authenticated role. */
const isDashboardLandingPath = (pathname) =>
  normalizePath(pathname) === normalizePath(PATH_DASHBOARD.general.app);

/** Authenticated utility pages that are not module-gated. */
const ALWAYS_ALLOWED_PATHS = [
  PATH_DASHBOARD.general.app,
  PATH_DASHBOARD.user.account,
  '/dashboard/notifications',
].map(normalizePath);

/**
 * Whether the logged-in user may open this dashboard path.
 * Uses SidebarConfig.hidden(user) — same rules as the menu (plus route-only settings rules).
 *
 * Exception: /dashboard/app (and a few utility paths) always allowed for authenticated users.
 * Other paths not listed in sidebar/route-only rules remain allowed (legacy/template pages).
 */
export function canAccessDashboardPath(user, pathname) {
  if (!user) return false;

  const current = normalizePath(pathname);

  if (ALWAYS_ALLOWED_PATHS.includes(current) || isDashboardLandingPath(current)) {
    return true;
  }

  const items = getSidebarPermissionItems();

  const matches = items
    .filter(
      (item) =>
        item.path &&
        (current === normalizePath(item.path) ||
          current.startsWith(`${normalizePath(item.path)}/`))
    )
    .sort((a, b) => b.path.length - a.path.length);

  if (matches.length === 0) {
    return true;
  }

  const rule = matches[0];
  if (typeof rule.hidden !== 'function') {
    return true;
  }

  // sidebar: hidden(user) === true means menu item is not shown → deny route
  return !rule.hidden(user);
}
